import React, { Dispatch, SetStateAction, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { RequirementContent } from '../../models/requirement.content.model';
import { accountingAccountSelected } from '../../scripts/list.selected.script';
import { legendValid } from '../../utils/legend.data.util';
import { prefixLegend } from '../../scripts/name.legend.script';
import { formatAccountingAccount } from '../../scripts/format.value.script';
import { accountingAccountRebate } from '../../scripts/item.account.script';

import AppLegend from './Legend';

export interface AppInputAccountingAccountProps {
  component?: string,
  options: {small: boolean, type: string, index: number | null, item: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}} | null},
  companyForUser: Company,
  accountingAccountsForCompany?: AccountingAccount[],
  accountingExpenseAccountsForCompany?: AccountingAccount[],
  accountingVatAccountsForCompany?: AccountingAccount[],
  accountingIncomeAccountsForCompany?: AccountingAccount[],
  accounts?: {value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}},
  contentAccounts?: {value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]},
  setAccounts?: Dispatch<SetStateAction<{value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}}>>,
  setContentAccounts?: Dispatch<SetStateAction<{value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]}>>,
  genericList?: AccountingAccount[],
  genericValue?: string,
  onSelectGenericAccount?: (id_account: string) => void,
};

const AppInputAccountingAccount: React.FunctionComponent<AppInputAccountingAccountProps> = ({component, options, companyForUser, accountingAccountsForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, accounts, contentAccounts, setAccounts, setContentAccounts, genericList, genericValue, onSelectGenericAccount}) => {
  const {lang} = StateLanguage()
  const isGeneric = !!(onSelectGenericAccount && genericList)

  const [search, setSearch] = useState<{text: string, list: AccountingAccount[]}>({text: '', list: []})

  const handleChangeSearchAccount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let current: AccountingAccount[] = accountingAccountList()
    let list: AccountingAccount[] = []
    let text: string = event.target.value

    if (text.length > 0) {
      for (let account of current) {
        if (account.code.indexOf(text) !== -1 || formatAccountingAccount(account, companyForUser.language).toLowerCase().indexOf(text.toLowerCase()) !== -1) {
          list.push(account)
        }
      }
    } else {
      list = current
    }

    setSearch({text: text, list: list})
  }

  const handleChangeAccountingAccount = (item: string) => {
    if (accounts && setAccounts) {
      setAccounts(accounts => ({value: {...accounts.value, [options.type as 'expense' | 'vat' | 'income']: item}, valid: {...accounts.valid, [options.type as 'expense' | 'vat' | 'income']: true}}))
      legendValid(`${prefixLegend(component)}legend-account-${options.type}-required`)
    }
  }

  const handleChangeAccountingAccounts = (item: AccountingAccount) => {
    if (contentAccounts && setContentAccounts) {
      let tempContentAccountsValue = contentAccounts.value.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          if (accountingAccountsForCompany && accountingIncomeAccountsForCompany) {
            if (options.type === 'debit') {
              temp_item.debit.accounting_account = item.id
            }
            if (options.type === 'credit') {
              temp_item.debit.accounting_account = ((temp_item.content.rebate && temp_item.content.rebate.type !== 'none') || (!temp_item.content.rebate && temp_item.content.rebate_amount > 0)) ? accountingAccountRebate(accountingAccountsForCompany, accountingIncomeAccountsForCompany, item.id) : ''
              temp_item.credit.accounting_account = item.id
            }
          }
          if (accountingExpenseAccountsForCompany) {
            if (options.type === 'debit') {
              temp_item.debit.accounting_account = item.id
            }
            if (options.type === 'credit') {
              temp_item.credit.accounting_account = item.id
            }
          }
        }
        return temp_item
      })
      let tempContentAccountsValid = contentAccounts.valid.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item = true
        }
        return temp_item
      })

      setContentAccounts({value: tempContentAccountsValue, valid: tempContentAccountsValid})
    }
  }

  function nameAccountingAccount(): string {
    switch (options.type) {
      case 'expense':
        return "account-expense"
      case 'vat':
        return "account-vat"
      case 'income':
        return "account-income"
      default:
        return "account"
    }
  }

  function accountingAccountList(): AccountingAccount[] {
    if (isGeneric) return genericList ?? []
    switch (options.type) {
      case 'expense':
        return accountingExpenseAccountsForCompany || []
      case 'vat':
        return accountingVatAccountsForCompany || []
      case 'income':
        return accountingIncomeAccountsForCompany || []
      default:
        return []
    }
  }

  function filteredContentAccounts(kind: 'income' | 'expense'): AccountingAccount[] {
    const source = (kind === 'income' ? accountingIncomeAccountsForCompany : accountingExpenseAccountsForCompany) || []
    const text = search.text.trim().toLowerCase()
    if (!text) return source
    return source.filter(a =>
      a.code.toLowerCase().includes(text) || formatAccountingAccount(a, companyForUser.language).toLowerCase().includes(text)
    )
  }

  function uploadDropdown() {
    setSearch({text: '', list: accountingAccountList()})
  }

  return (
    <>
      <Dropdown onToggle={uploadDropdown}>
        <Dropdown.Toggle variant={`select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open ${options.small ? "w-350px" : "w-100"} p-0`}>
          <span className="selection">
            <span className={`select2-selection select2-selection--single form-select ${options.small && "form-select-sm"}`}>
              <span className="select2-selection__rendered" role="textbox">
                { isGeneric &&
                  <>
                    {genericValue ? accountingAccountSelected(genericList ?? [], genericValue, companyForUser.language) : lang.labels.selectOption}
                  </>
                }
                { accounts && setAccounts &&
                  <>
                    {accounts.valid[options.type as 'expense' | 'vat' | 'income'] ? accountingAccountSelected(accountingAccountList(), accounts.value[options.type as 'expense' | 'vat' | 'income'], companyForUser.language) : lang.labels.selectOption}
                  </>
                }
                { options.index !== null && options.item !== null && accountingIncomeAccountsForCompany && contentAccounts && setContentAccounts &&
                  <>
                    {contentAccounts.valid[options.index] ? accountingAccountSelected(accountingIncomeAccountsForCompany, options.item.content.price_unit < 0 ? options.item.debit.accounting_account : options.item.credit.accounting_account, companyForUser.language) : lang.labels.selectOption}
                  </>
                }
                { options.index !== null && options.item !== null && accountingExpenseAccountsForCompany && contentAccounts && setContentAccounts &&
                  <>
                    {contentAccounts.valid[options.index] ? accountingAccountSelected(accountingExpenseAccountsForCompany, options.item.content.price_unit < 0 ? options.item.credit.accounting_account : options.item.debit.accounting_account, companyForUser.language) : lang.labels.selectOption}
                  </>
                }
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open" renderOnMount popperConfig={{ strategy: 'fixed', modifiers: [{ name: 'sameWidth', enabled: true, phase: 'beforeWrite' as const, requires: ['computeStyles'], fn: ({ state }: any) => { state.styles.popper.width = `${state.rects.reference.width}px` }, effect: ({ state }: any) => { state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px` } }] }}>
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox" style={{maxHeight: '300px', overflowY: 'auto'}}>
                { accountingIncomeAccountsForCompany &&
                  <li className={`select2-results__option select2-results__option--selectable ${accountingIncomeAccountsForCompany.length > 0 && "d-none"}`}>
                    <Dropdown.Item bsPrefix="select2-results__option__text py-2">{lang.labels.selectOption}</Dropdown.Item>
                  </li>
                }
                { accountingExpenseAccountsForCompany &&
                  <li className={`select2-results__option select2-results__option--selectable ${accountingExpenseAccountsForCompany.length > 0 && "d-none"}`}>
                    <Dropdown.Item bsPrefix="select2-results__option__text py-2">{lang.labels.selectOption}</Dropdown.Item>
                  </li>
                }
                { isGeneric &&
                  <>
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" value={search.text} onChange={handleChangeSearchAccount} />
                    </span>
                    { (search.text.length > 0 ? search.list : (genericList ?? [])).length > 0
                      ?
                      <>
                        { (search.text.length > 0 ? search.list : (genericList ?? [])).map((item, index) => (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === genericValue && "select2-results__option--selected"}`} role="option" aria-selected={item.id === genericValue}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => onSelectGenericAccount!(item.id)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                          </li>
                        ))}
                      </>
                      :
                      <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                    }
                  </>
                }
                { accounts && setAccounts &&
                  <>
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" name={nameAccountingAccount()} value={search.text} onChange={handleChangeSearchAccount} />
                    </span>
                    { search.list.length > 0
                      ?
                      <>
                        { search.list.map (( (item, index) => { return (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accounts.value.expense && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accounts.value.expense}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingAccount(item.id)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                          </li>
                        )}))}
                      </>
                      :
                      <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                    }
                  </>
                }
                { options.item !== null && accountingIncomeAccountsForCompany &&
                  <>
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" value={search.text} onChange={handleChangeSearchAccount} />
                    </span>
                    { filteredContentAccounts('income').length > 0
                      ?
                      <>
                        { filteredContentAccounts('income').map (( (item, index) => { return (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === (options.item!.content.price_unit < 0 ? options.item!.debit.accounting_account : options.item!.credit.accounting_account) && "select2-results__option--selected"}`} role="option" aria-selected={item.id === (options.item!.content.price_unit < 0 ? options.item!.debit.accounting_account : options.item!.credit.accounting_account)}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingAccounts(item)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                          </li>
                        )}))}
                      </>
                      :
                      <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                    }
                  </>
                }
                { options.item !== null && accountingExpenseAccountsForCompany &&
                  <>
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" value={search.text} onChange={handleChangeSearchAccount} />
                    </span>
                    { filteredContentAccounts('expense').length > 0
                      ?
                      <>
                        { filteredContentAccounts('expense').map (( (item, index) => { return (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === (options.item!.content.price_unit < 0 ? options.item!.credit.accounting_account : options.item!.debit.accounting_account) && "select2-results__option--selected"}`} role="option" aria-selected={item.id === (options.item!.content.price_unit < 0 ? options.item!.credit.accounting_account : options.item!.debit.accounting_account)}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingAccounts(item)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                          </li>
                        )}))}
                      </>
                      :
                      <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                    }
                  </>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { accounts && setAccounts &&
        <AppLegend component={component} attribute={{check: accounts.valid.expense, name: nameAccountingAccount(), index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      }
    </>
  )
}

export default AppInputAccountingAccount;
